import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { useBootstrap } from '../../../hooks/use-bootstrap';
import { useSyncUserLanguage } from '../../../lib/i18n/useLanguagePreference';
import { dispatchAuthExpired } from '../../../lib/api';
import { Spinner } from '../ui/spinner';

interface AppBootstrapProps {
  children: ReactNode;
}

/**
 * Gates the authenticated layout on a coordinated load of the data the app
 * needs before any protected route renders. #1429: this is now a SINGLE
 * request — GET /bootstrap — that returns user (+ org + memberships),
 * enterprise features, permissions, company info, company preferences, and the
 * dashboard-permissions matrix in one shot, and seeds each individual React
 * Query cache so the standalone hooks become cache hits (no duplicate calls).
 *
 * A 401 is caught by the axios interceptor, which fires AUTH_EXPIRED_EVENT;
 * the SessionExpiryListener mounted higher up handles the navigation. While
 * that's in flight we render nothing to avoid a flash of a half-hydrated
 * layout. Any other error surfaces a recoverable retry.
 */
export function AppBootstrap({ children }: AppBootstrapProps) {
  const { t } = useTranslation();
  const bootstrap = useBootstrap();
  // Apply the user's effective UI language (personal override → org default →
  // English) once the bootstrap payload has populated the auth store.
  useSyncUserLanguage();

  const status = isAxiosError(bootstrap.error)
    ? bootstrap.error.response?.status
    : undefined;
  const errorCode = isAxiosError(bootstrap.error)
    ? (bootstrap.error.response?.data as { error?: { code?: string } } | undefined)
        ?.error?.code
    : undefined;
  // A bootstrap 404 with code NOT_FOUND means the access token references a
  // user that no longer exists in this database (a stale/invalid session — e.g.
  // after a local DB reseed, a DATABASE_URL switch, or the user being deleted).
  // The axios interceptor only fires AUTH_EXPIRED on a 401, so without this the
  // app would sit forever on the "temporary — please retry" boundary, replaying
  // the same dead token. Treat it like a 401: clear the session + redirect to
  // login so a stale token self-heals.
  const sessionInvalid =
    bootstrap.isError && status === 404 && errorCode === 'NOT_FOUND';

  useEffect(() => {
    if (sessionInvalid) {
      dispatchAuthExpired('session-invalid');
    }
  }, [sessionInvalid]);

  // Self-heal the recoverable error. Even after the query's own retry budget is
  // exhausted (server slower to warm up than ~30s), keep quietly refetching
  // while the boundary is showing so the app recovers ON ITS OWN once the API
  // is reachable — the operator no longer has to sit clicking "retry" on a cold
  // start. Excludes the redirect cases (401 / invalid session) and pauses while
  // a fetch is already in flight. The `refetch` identity is stable across
  // renders, so the interval isn't torn down and recreated each tick.
  const recoverableError = bootstrap.isError && status !== 401 && !sessionInvalid;
  const { refetch, isFetching } = bootstrap;
  useEffect(() => {
    if (!recoverableError) return;
    const id = setInterval(() => {
      if (!isFetching) refetch();
    }, 4000);
    return () => clearInterval(id);
  }, [recoverableError, isFetching, refetch]);

  if (bootstrap.isSuccess) {
    return <>{children}</>;
  }

  // A 401 (interceptor) or a NOT_FOUND 404 (handled above) both redirect to
  // login via SessionExpiryListener. Render nothing only while that redirect is
  // in flight. For ANY OTHER error (5xx / network blip) the interceptor does
  // NOT redirect — returning null would leave a blank white page that only a
  // manual refresh clears. Surface a recoverable error.
  if (bootstrap.isError) {
    if (status === 401 || sessionInvalid) {
      return null;
    }
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-3 max-w-md text-center px-4">
          <p className="text-sm text-gray-700">
            {t('components.appBootstrap.loadError')}
          </p>
          {/* The retry loop above keeps trying on its own; the button just lets
              an impatient operator force one immediately. */}
          <button
            type="button"
            onClick={() => bootstrap.refetch()}
            disabled={isFetching}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-60"
          >
            {isFetching
              ? t('components.appBootstrap.retrying')
              : t('components.appBootstrap.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="lg" />
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      </div>
    </div>
  );
}
